import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { VossProductGroupTypeText } from '../../Constants/VOSS/vossProductGroupType';
import { InfoLabel } from '../Badges/InfoLabel';
import { ButtonSize } from '../Fundamentals/Button';
import SkynetIconButton from '../Fundamentals/IconButton/SkynetIconButton';
import type { CalculatedGroup } from './PriceSimulationWorksheet.types';
import WorksheetTotal from './WorksheetTotal';
import { isFromButton } from './worksheetEvents';

interface WorksheetGroupRowsProps {
    group: CalculatedGroup;
    isFirst: boolean;
    isCollapsed: boolean;
    onToggle: () => void;
    children: ReactNode;
}

const WorksheetGroupRows = ({ group, isFirst, isCollapsed, onToggle, children }: WorksheetGroupRowsProps) => {
    const { t } = useTranslation();

    return (
        <tbody>
            <tr
                className="worksheet-group-row cursorpointer"
                onClick={(e) => {
                    if (!isFromButton(e)) onToggle();
                }}
            >
                <td colSpan={4} className={isFirst ? undefined : 'border-top'}>
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-10 pl-16">
                            <span className="semibold">{group.groupName}</span>
                            <InfoLabel
                                type={group.isIncluded ? 'success' : undefined}
                                title={t(VossProductGroupTypeText[group.productGroupType] || 'vossProductGroupType.included')}
                            />
                        </div>
                        <div className="d-flex align-items-center gap-12">
                            {group.groupHasAnnualValue ? (
                                <WorksheetTotal
                                    label={t('priceSimulation.groupAnnualValue')}
                                    original={group.groupCurrentAnnualValue}
                                    next={group.groupNewAnnualValue}
                                    hasQuantity={group.groupHasQuantity}
                                    changeClassName="ml-8"
                                />
                            ) : (
                                <span className="body-sm text-secondary">{t('priceSimulation.noAnnualValue')}</span>
                            )}
                            <SkynetIconButton
                                icon="chevron-right"
                                iconActive="chevron-down"
                                expanded={!isCollapsed}
                                onClick={onToggle}
                                size={ButtonSize.SMALL}
                                label={t('priceSimulation.toggleSection')}
                            />
                        </div>
                    </div>
                </td>
            </tr>

            {!isCollapsed && children}
        </tbody>
    );
};

export default WorksheetGroupRows;
